import {
  type Experiment,
  type ExperimentEvaluationOptions,
  useStatsigClient,
} from "@statsig/react-bindings";
import {
  registerStatsigExperiment,
  trackExperiment,
} from "utils/tracking/mixpanel";

export interface StatsigExperimentValues {
  value: Record<string, unknown>;
  groupName: string | null;
  isControlGroup?: boolean;
}

export interface StatsigBaseExperimentData {
  name: string;
  groupName: string | null;
}

export interface StatsigValueExperimentData<ResValue = Record<string, unknown>>
  extends StatsigBaseExperimentData {
  value: ResValue;
  keyValue?: never;
}

export interface StatsigKeyValueExperimentData
  extends StatsigBaseExperimentData {
  value?: never;
  keyValue: boolean | string | null | undefined;
}

export type ExperimentData =
  | StatsigValueExperimentData
  | StatsigKeyValueExperimentData;

export interface BaseExperimentKeyProps {
  experimentName: string;
  defaultValue?: string | boolean | null;
}

export interface ValueExperimentKeyProps extends BaseExperimentKeyProps {
  experimentKey?: never;
}

export interface KeyValueExperimentKeyProps extends BaseExperimentKeyProps {
  experimentKey: string;
}

export type StatsigExperimentKeyProps =
  | ValueExperimentKeyProps
  | KeyValueExperimentKeyProps;

export function useStatsigExperiment<ValueType extends Experiment["value"]>(
  options?: ExperimentEvaluationOptions & {
    dontTrack?: boolean;
    trackViewOnAllocation?: boolean;
  },
) {
  const { client, getExperiment, logEvent } = useStatsigClient();
  const context = client.getContext();

  function trackableExperiment(
    value: Record<string, unknown>,
    ruleID: Experiment["ruleID"],
    groupName: Experiment["groupName"],
  ): groupName is string {
    return Boolean(
      Object.keys(value).length &&
        !options?.dontTrack &&
        groupName &&
        !ruleID?.toLowerCase()?.includes("override"),
    );
  }

  function registerExperiment({
    name,
    ruleID,
    groupName,
    value,
  }: Pick<Experiment, "ruleID" | "groupName" | "name" | "value">) {
    if (trackableExperiment(value, ruleID, groupName)) {
      registerStatsigExperiment(name, groupName, context.stableID);
      if (options?.trackViewOnAllocation) {
        trackExperimentView(name, { ruleID, groupName, value });
      }
    }
  }

  function getExperimentValue<SpecificValueType = ValueType>(
    name: Experiment["name"],
    defaultValue?: SpecificValueType,
  ) {
    const expData = getExperiment(name, options);
    registerExperiment(expData);
    if (options?.trackViewOnAllocation) {
      trackExperimentView(name, expData);
    }

    return (expData.value || defaultValue) as SpecificValueType;
  }

  function trackExperimentView(
    name: Experiment["name"],
    expData?: Pick<Experiment, "ruleID" | "groupName" | "value">,
  ) {
    const { value, groupName, ruleID } =
      expData ?? getExperiment(name, options);
    if (trackableExperiment(value, ruleID, groupName)) {
      const eventData = {
        experiment_name: name,
        experiment_variant: groupName,
        ...(context.stableID && { experiment_stable_id: context.stableID }),
      };
      trackExperiment("Experiment Viewed", eventData, {
        onceWithUniqueKey: name + context.session.data.sessionID,
      });
      logEvent("Experiment Viewed", name, eventData);
    }
  }

  return {
    getExperimentValue,
    registerExperiment,
    trackExperimentView,
  };
}
